// =================================================================================================
// >>> **THE /talk FUNNEL BOARD — HER ORDER, 2026-09-05: "her analytics page is her only eyes."** <<<
//
// *"real columns for THIS funnel, with real counts reconciled against the ledgers (not the broken
//  board): saw -> painted -> reading delivered -> spoke to Lala -> signup. Each a real number, today
//  and last-hour, so she sees exactly where people are and where they stop."*
//
// **THIS REPLACES `funnelLalaBoard.js`, WHICH IS DELETED RATHER THAN HIDDEN.** That board drew five
// rungs off the ALLOTMENT COLUMNS — *given the five, started, turns used, at the wall, converted* —
// and two of her five names had no reader at all and printed an apology instead. It could not answer
// the one question she has actually been asking for a fortnight: **did a real woman see the page.**
//
// >>> **IT IMPORTS NOTHING, WHICH IS WHAT LETS A FAST FIXTURE DRIVE EVERY STATE** (R146), and the
// SURFACE composes no arithmetic of its own: a screen that does its own sums is a second answer key
// (R28/R29), and on this page the second answer is the one she would act on.
//
// ---------------------------------------------------------------------------------------------
// >>> **EVERY RUNG NAMES THE TABLE IT IS READ FROM, ON THE PAGE, IN HER OWN WORDS.** <<<
//
// Her clause: *"one reader shared with the ledger so the board can never disagree with reality
// again (the 2.7x over-count and the leads-vs-visits lie both killed)."* A column whose source is
// not written down is a column somebody re-derives differently six weeks later, which is exactly how
// both of those lies happened. So the source is part of the row and it is printed.
// =================================================================================================

/**
 * >>> **THE CHAIN, IN THE ORDER A WOMAN ACTUALLY WALKS IT.** <<<
 *
 * **`saw` AND `painted` BEGIN AT THE BEACON AND NOT BEFORE**, and that is stated rather than hidden:
 * `client_errors` has existed since 2026-09-05, so any window reaching back past that has NO
 * denominator for the first two rungs. Drawing them as zero would say *nobody came*, which is the
 * single most misleading thing this page could say (R141), so they draw a dash and print why.
 */
export const TALK_FUNNEL_RUNGS = [
  {
    key: "saw",
    label: "Saw the page",
    source: "client_errors — one row per /talk page load, reported by the page itself",
    of: "",
  },
  {
    key: "painted",
    label: "It actually loaded",
    source: "client_errors — the page reported `#root` with children inside 2.5s",
    of: "saw",
  },
  {
    key: "signup",
    label: "Gave her details",
    source: "users — a seat minted with `landing_tag` set, which is the funnel's own door",
    of: "painted",
  },
  {
    key: "reading",
    label: "Got her reading",
    source: "reports — a `funnel-reading` saved with real data",
    of: "signup",
  },
  {
    key: "spoke",
    label: "Spoke to Lala",
    source: "lala_messages — a message of HER OWN, role `user`, never a greeting",
    of: "reading",
  },
];

/** The beacon's first row. Before this, `saw` and `painted` have no denominator and say so. */
export const BEACON_LIVE_FROM = "2026-09-05";

export const TALK_FUNNEL_COVERAGE_NOTE =
  "Saw and It actually loaded are reported by the page itself and begin on 5 September 2026, when "
  + "that reporting shipped. A window reaching back before then shows a dash for those two rather "
  + "than a zero, because nobody came and nobody was counted are different facts.";

const n = (v) => (typeof v === "number" && Number.isFinite(v) && v >= 0 ? Math.round(v) : null);

/**
 * >>> **EACH RATE IS A SHARE OF THE RUNG DIRECTLY ABOVE IT, NEVER OF THE TOP.** <<< `spoke / saw`
 * mixes populations that cannot be compared — a woman who never got a page cannot speak — so it
 * reads low for ever and every fix looks like it changed nothing. This is the one arithmetic rule
 * the old board got right and it is kept verbatim.
 *
 * **AND A RATE OVER NOTHING IS `null`, NEVER 0% AND NEVER 100% (R141).**
 */
/**
 * >>> **THE LOADS THIS HOUSE CAN ACTUALLY SPEAK ABOUT.** <<< `saw` counts every page load; some of
 * those women left inside two and a half seconds, before the page could be judged either way.
 *
 * **THEY ARE COUNTED, REPORTED, AND KEPT OUT OF THE PAINT RATE.** Counting a bounce as a page that
 * failed to load slanders a working site — it is what turned 42 loads and 27 paints into "36% of
 * arrivals are still blank" on this board's first live reading, which was false. Counting her as a
 * paint would flatter a broken one. Neither, so she comes off the denominator and stays on the page.
 */
export function talkFunnelDecided(board) {
  const saw = n(board?.saw);
  const left = n(board?.left);
  if (saw === null) return null;
  return left === null ? saw : Math.max(0, saw - left);
}

export function talkFunnelRows(board) {
  if (!board || typeof board !== "object") return [];
  const counts = new Map(TALK_FUNNEL_RUNGS.map((r) => [r.key, n(board[r.key])]));
  const decided = talkFunnelDecided(board);
  return TALK_FUNNEL_RUNGS.map((rung) => {
    const count = counts.get(rung.key);
    // The paint rung alone divides by what could be JUDGED, not by everyone who arrived.
    const above = rung.key === "painted" ? decided : (rung.of ? counts.get(rung.of) : null);
    const pct = (count === null || above === null || above === 0) ? null : Math.round((count / above) * 100);
    return {
      key: rung.key, label: rung.label, source: rung.source, count, pct,
      of: rung.key === "painted" ? "loads we can judge" : rung.of,
    };
  });
}

/**
 * >>> **THE SENTENCE AT THE TOP, IN REAL WORDS — HER CLAUSE 4.** <<< *"X real people got a reading
 * today."*
 *
 * It says NOTHING rather than a number it cannot stand behind, because this line is the one she
 * would quote to somebody, and a sentence is a receipt like any other (R141).
 */
export function talkFunnelSentence(board) {
  const reading = n(board?.reading);
  if (reading === null) return "";
  if (reading === 0) return "Nobody has finished a reading yet today.";
  const spoke = n(board?.spoke);
  const one = reading === 1;
  const head = `${reading} real ${one ? "person" : "people"} got a reading today`;
  if (spoke === null || spoke === 0) return `${head}.`;
  return `${head}, and ${spoke} of them went on to talk to Lala.`;
}

/**
 * **WHERE THEY STOP, WHICH IS THE THING SHE ASKED THIS PAGE TO SHOW HER.** The biggest fall between
 * two neighbouring rungs, named. Nothing where the chain is too thin to have a worst step: on two
 * arrivals every drop is 50% and pointing at one of them is noise dressed as a finding.
 */
export function talkFunnelWorstDrop(board, { floor = 10 } = {}) {
  const rows = talkFunnelRows(board);
  const top = rows[0]?.count;
  if (top === null || top === undefined || top < floor) return null;
  let worst = null;
  for (const r of rows) {
    if (!r.of || r.pct === null) continue;
    const lost = 100 - r.pct;
    if (lost <= 0) continue;
    if (!worst || lost > worst.lost) worst = { lost, from: r.of, to: r.key, label: r.label, pct: r.pct };
  }
  return worst;
}

// =================================================================================================
// >>> **THE PER-SOURCE RUNG'S LAW — HER ORDER, 2026-09-08: *the number tells her.*** <<<
//
// *"so she never again has to wonder whether traffic is real. A source converting at 0.05% while
//  direct converts at 50% flags itself on the screen she actually looks at."*
//
// **THE FLAGGING IS THE FEATURE.** A table of rates is still a table she has to divide in her head,
// and the whole reason this instrument was missing for five days is that nobody did the division.
// So the law names the failing source in a sentence, and the surface prints the sentence.
// =================================================================================================

/**
 * >>> **BELOW THIS MANY ARRIVALS A SOURCE IS NOT JUDGED — IT IS NOT A PASS EITHER.** <<<
 *
 * Nine arrivals and no signups is a Tuesday, not a finding. Direct sends 15-40 a day and converts at
 * 40-50%: it must never be flagged for a quiet morning. The floor is on ARRIVALS because that is the
 * denominator the rate is drawn from.
 */
export const TALK_SOURCE_FLOOR = 40;

/**
 * >>> **HOW FAR BELOW THE BEST SOURCE A SOURCE HAS TO SIT BEFORE IT IS CALLED OUT.** <<<
 *
 * A fifth. Not a fixed percentage, because the honest question is never *is 0.05% low* — it is *is
 * this source converting like the others on the same page in the same hours*. That comparison is
 * what makes the number self-evidently about the TRAFFIC and not about the page: the page is the
 * one thing every source shares.
 */
export const TALK_SOURCE_LAGGARD_RATIO = 0.2;

const rate = (num, den) => (den > 0 ? Number(((100 * num) / den).toFixed(2)) : null);

/**
 * One row per source, best conversion first. **A rate over nothing is `null`, never 0%** (R141), and
 * a source under the floor carries `judged: false` so the surface can draw it without convicting it.
 */
export function talkFunnelSourceRows(sources) {
  const list = Array.isArray(sources) ? sources : [];
  const rows = list
    .filter((s) => s && typeof s === "object" && n(s.arrivals) !== null)
    .map((s) => {
      const arrivals = n(s.arrivals) ?? 0;
      const gave = n(s.gaveDetails) ?? 0;
      const judged = arrivals >= TALK_SOURCE_FLOOR;
      return {
        source: String(s.source || "(none)"),
        arrivals,
        gaveDetails: gave,
        reading: n(s.reading) ?? 0,
        spoke: n(s.spoke) ?? 0,
        // The rate that matters, and the one the whole five days turned on.
        pct: judged ? rate(gave, arrivals) : null,
        judged,
        why: judged ? "" : `only ${arrivals} arrivals, and ${TALK_SOURCE_FLOOR} is the floor under a verdict`,
      };
    });
  // Judged rows first, best rate first; unjudged rows after, biggest first, so a source that is
  // merely quiet never sits at the bottom looking like the worst one.
  return rows.sort((a, b) => {
    if (a.judged !== b.judged) return a.judged ? -1 : 1;
    if (a.judged) return (b.pct ?? -1) - (a.pct ?? -1);
    return b.arrivals - a.arrivals;
  });
}

/**
 * >>> **THE SENTENCE THAT DOES THE DIVISION FOR HER.** <<<
 *
 * Answers `null` when there is nothing to say — fewer than two judged sources, or every source
 * converting alike — because a board that always has an accusation on it is a board she stops
 * reading.
 */
export function talkFunnelSourceVerdict(sources) {
  const rows = talkFunnelSourceRows(sources).filter((r) => r.judged && r.pct !== null);
  if (rows.length < 2) return null;
  const best = rows[0];
  const laggards = rows.slice(1).filter((r) => r.pct <= best.pct * TALK_SOURCE_LAGGARD_RATIO);
  if (!laggards.length) return null;
  // The worst one carries the sentence; the rest are counted, so four bad sources do not become
  // four alarms she has to read separately.
  const worst = laggards[laggards.length - 1];
  const wasted = laggards.reduce((sum, r) => sum + r.arrivals, 0);
  return {
    best: best.source, bestPct: best.pct,
    worst: worst.source, worstPct: worst.pct,
    laggards: laggards.map((r) => r.source),
    wastedArrivals: wasted,
    sentence: `${worst.source} sent ${wasted.toLocaleString("en-US")} arrivals and converted ${worst.pct}%, `
      + `while ${best.source} converted ${best.pct}% on the same page in the same hours. `
      + `That gap is about the TRAFFIC, not the page — the page is the one thing both of them share.`,
  };
}

/** What it would take to show paint per source too. Named so the gap stays an ask, never a silence. */
export const TALK_SOURCE_PAINT_NOTE =
  "Saw and It actually loaded cannot be split by source: the page's own reporter mints its own visit "
  + "id and nothing links it to a visitor row. Everything below Gave her details can be split, and is.";
